import React                     from 'react';
import { observer }              from 'mobx-react-lite';
import { useNavigate }           from 'react-router-dom';
import View                      from '@widesk-ui/components/View';
import Text                      from '@widesk-ui/components/Text';
import Tag                       from '@widesk-ui/components/Tag';
import usePagedCollection        from '@widesk-core/hooks/usePagedCollection';
import ProjectModel              from '@/models/services/map/ProjectModel';
import FileImage                 from '@/components/FileImage';
import appStore                  from '@/stores/appStore';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import useDesignTokens           from '@/hooks/useDesignTokens';
import { SwitcherMenu }          from '@/TokamapEditor/ux/SwitcherMenu';
import { SwitcherMenuRow }       from '@/TokamapEditor/ux/SwitcherMenu';
import { SwitcherMenuCheck }     from '@/TokamapEditor/ux/SwitcherMenu';

const THUMBNAIL_SIZE = 32;

// `display: block` : `View` rend un conteneur flex, où `text-overflow` ne produit aucun point.
const ellipsis: React.CSSProperties = {
	display     : 'block',
	whiteSpace  : 'nowrap',
	overflow    : 'hidden',
	textOverflow: 'ellipsis',
};

/**
 * Contenu du menu ouvert par le segment « projet » du fil d'ariane : la liste des projets du compte,
 * pour passer de l'un à l'autre sans repasser par l'accueil. Pendant du menu des cartes
 * (`MapMenu`), un cran au-dessus dans la hiérarchie — ils partagent leur coquille.
 */
const ProjectMenu = observer((props: { onSelect: () => void; }) => {
	const projectCollection = usePagedCollection(ProjectModel);
	const navigate = useNavigate();
	const tokens = useDesignTokens();
	const activeRef = React.useRef<HTMLDivElement | null>(null);
	const [search, setSearch] = React.useState<string>();

	const currentProjectId = currentProjectEditorStore.get?.project.id;

	React.useEffect(() => {
		(async () => {
			await projectCollection
				.setItemsPerPage(100)
				.setSort('updatedAt', 'desc')
				.setRequiredFilter('owner.accountUrn', appStore.accountUrn)
				.list();

			activeRef.current?.scrollIntoView({ behavior: 'auto', block: 'nearest' });
		})();
	}, [appStore.accountUrn]);

	// Filtrage local, comme sur l'accueil : l'API n'expose pas de filtre de recherche sur les projets.
	const filteredProjects = search
		? projectCollection.models.filter(p => p.title?.toLowerCase().includes(search.toLowerCase()))
		: projectCollection.models;

	return (
		<SwitcherMenu
			title="Vos projets"
			count={projectCollection.isLoaded ? projectCollection.total : undefined}
			searchPlaceholder="Rechercher un projet…"
			onSearch={setSearch}
			loading={!projectCollection.isLoaded}
		>
			{filteredProjects.map(project => (
				<ProjectRow
					key={project.id}
					project={project}
					active={project.id === currentProjectId}
					innerRef={project.id === currentProjectId ? activeRef : undefined}
					onSelect={() => {
						props.onSelect();

						// Rester sur place plutôt que de rejouer la route : `ProjectProvider` recrée un
						// `ProjectEditorStore` à chaque changement de `projectId`, y compris pour le même.
						if (project.id === currentProjectId) return;

						// Chemin absolu : `navigate` résout le relatif contre `/editor/:projectId`.
						navigate(`/editor/${project.id}`);
					}}
				/>
			))}

			{!filteredProjects.length && (
				<View p="sm" center>
					<Text color={tokens.text.muted}>
						{search ? `Aucun projet ne correspond à « ${search} »` : `Aucun projet`}
					</Text>
				</View>
			)}
		</SwitcherMenu>
	);
});

const ProjectRow = observer((props: {
	project: ProjectModel;
	active: boolean;
	innerRef?: React.Ref<HTMLDivElement>;
	onSelect: () => void;
}) => {
	const { project, active, innerRef } = props;
	const tokens = useDesignTokens();

	// Résolu en une seule requête pour toute la collection (voir `reverseResolvable`), et non un
	// appel par ligne comme le ferait `ProjectImage`.
	const imageFile = project.imageFile;

	// Archivé comme verrouillé : même lecture que sur l'accueil, le projet n'est pas ouvrable.
	const isInactive = project.projectStatus.reference !== 'ENABLED';

	return (
		<SwitcherMenuRow active={active} innerRef={innerRef} disabled={isInactive} onClick={props.onSelect}>
			<View centerV>
				<FileImage
					key={imageFile.urn}
					loading={!imageFile.isLoaded}
					file={imageFile}
					width={THUMBNAIL_SIZE}
					height={THUMBNAIL_SIZE}
					fit="cover"
					borderRadius={6}
					preview={false}
					placeholder
				/>
			</View>

			{/* Pas de seconde ligne « mis à jour le… » : `updatedAt` est celui de l'enregistrement projet
			    (titre, image), il ne bouge pas quand on édite les cartes ou les lieux — la date affichée
			    aurait donc contredit ce qu'on vient de faire. La vraie dernière modification d'un projet
			    demande une requête par projet (voir `ProjectEditorStore.lastModification`), trop cher
			    pour cette liste. */}
			<View flex row gap="xs" centerV style={{ minWidth: 0 }}>
				{active && <SwitcherMenuCheck />}

				{/* Même information que sur les cartes de l'accueil, et sous la même forme : la date de
				    création, sans l'heure — elle situe le projet, l'heure n'apprend rien. */}
				<View flex gap={2} style={{ minWidth: 0 }}>
					<View row gap="xs" centerV style={{ minWidth: 0 }}>
						<View flex bold style={{ ...ellipsis, minWidth: 0 }}>
							{project.title || 'Sans titre'}
						</View>

						{/* `flexShrink: 0` : sinon c'est le tag qui se comprime, au lieu du titre. */}
						{isInactive && (
							<Tag color={project.projectStatus.color} style={{ flexShrink: 0, marginInlineEnd: 0 }}>
								{project.projectStatus.label}
							</Tag>
						)}
					</View>

					<View size="small" color={tokens.text.muted}>
						Créé le {project.createdAt.format('L')}
					</View>
				</View>
			</View>
		</SwitcherMenuRow>
	);
});

export default ProjectMenu;
